import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { ProjectFinanceService } from 'src/app/services/api/project-finance.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CommonService } from 'src/app/services/common.service';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import {
  PROJECT_FINANCE_DEFAULT_CURRENCY,
  PROJECT_OTHER_COST_CATEGORY_LABELS,
  PROJECT_FINANCE_PROFITABLE_STATUS,
  ProjectFinanceAttachment,
  ProjectFinanceBalance,
  ProjectFinanceCost,
  ProjectFinanceOtherCost
} from 'src/app/models/entities/project-finance';

@Component({
  selector: 'projects-finance-analysis',
  imports: [LoaderComponent, NgbTooltipModule, CurrencyPipe],
  templateUrl: './projects-finance-analysis.component.html',
  styleUrl: './projects-finance-analysis.component.scss'
})
export class ProjectsFinanceAnalysisComponent implements OnInit, OnDestroy {
  @Input() projectId!: number;

  testingCosts: ProjectFinanceCost[] = [];
  otherCosts: ProjectFinanceOtherCost[] = [];
  balance: ProjectFinanceBalance | null = null;

  canModify = false;
  canDelete = false;
  isLoading = false;

  readonly currency = PROJECT_FINANCE_DEFAULT_CURRENCY;

  private destroy$ = new Subject<void>();

  constructor(
    private projectFinanceService: ProjectFinanceService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService,
    private commonService: CommonService,
    private confirmationDialogService: ConfirmationDialogService,
    private mainLayoutService: MainLayoutService,
    private routeService: RouteService
  ) { }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(profileCode, 'PROJECTS_FINANCE', 'modify');
        this.canDelete = this.accessControlService.hasFeatureActionAccess(profileCode, 'PROJECTS_FINANCE', 'delete');
      });
    this.loadAnalysis();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get testingCostsSubtotal(): number {
    return this.testingCosts.reduce((total, cost) => total + (Number(cost.rowTotal) || 0), 0);
  }

  get otherCostsSubtotal(): number {
    return this.otherCosts.reduce((total, cost) => total + (Number(cost.amount) || 0), 0);
  }

  formatDate(value: string | null | undefined, withTime: boolean = false): string {
    return this.commonService.formatDateValue(value, withTime);
  }

  get hasActionsColumn(): boolean {
    return this.canModify || this.canDelete;
  }

  get isProfitable(): boolean {
    return this.balance?.projectStatus === PROJECT_FINANCE_PROFITABLE_STATUS;
  }

  categoryLabel(category: string): string {
    return PROJECT_OTHER_COST_CATEGORY_LABELS[category as keyof typeof PROJECT_OTHER_COST_CATEGORY_LABELS] ?? category;
  }

  resourceName(cost: ProjectFinanceCost): string {
    return cost.resourceUser ? `${cost.resourceUser.firstName} ${cost.resourceUser.lastName}`.trim() : '—';
  }

  addTestingCost(): void {
    this.routeService.projectsFinanceTestingCostCreate(this.projectId);
  }

  editTestingCost(cost: ProjectFinanceCost): void {
    this.routeService.projectsFinanceTestingCostUpdate(this.projectId, cost.projectFinanceCostId);
  }

  addOtherCost(): void {
    this.routeService.projectsFinanceOtherCostCreate(this.projectId);
  }

  editOtherCost(cost: ProjectFinanceOtherCost): void {
    this.routeService.projectsFinanceOtherCostUpdate(this.projectId, cost.projectFinanceOtherCostId);
  }

  confirmDeleteTestingCost(cost: ProjectFinanceCost): void {
    this.confirmationDialogService.open(
      `Are you sure you want to delete the testing cost of ${this.resourceName(cost)}?`,
      () => this.runDelete(this.projectFinanceService.deleteTestingCost(this.projectId, cost.projectFinanceCostId), 'Testing cost')
    );
  }

  confirmDeleteOtherCost(cost: ProjectFinanceOtherCost): void {
    this.confirmationDialogService.open(
      `Are you sure you want to delete the cost "${cost.description}"?`,
      () => this.runDelete(this.projectFinanceService.deleteOtherCost(this.projectId, cost.projectFinanceOtherCostId), 'Other cost')
    );
  }

  openAttachment(attachment: ProjectFinanceAttachment | null): void {
    if (!attachment?.url) {
      this.alertNotificationService.warning('File URL not available');
      return;
    }
    this.projectFinanceService.attachmentPublicUrl(attachment.url).subscribe({
      next: (publicUrl: string) => {
        if (!publicUrl) {
          this.alertNotificationService.error('Error downloading file');
          return;
        }
        window.open(publicUrl, '_blank');
      },
      error: (error: any) => {
        console.error('Error downloading the receipt:', error);
        this.alertNotificationService.error('Error downloading file');
      }
    });
  }

  private runDelete(request$: ReturnType<ProjectFinanceService['deleteTestingCost']>, label: string): void {
    this.isLoading = true;
    request$.subscribe({
      next: () => {
        this.alertNotificationService.success(`${label} deleted successfully`);
        this.loadAnalysis();
      },
      error: (error) => {
        this.isLoading = false;
        console.error(`Error deleting ${label.toLowerCase()}:`, error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, `Error deleting ${label.toLowerCase()}`));
      }
    });
  }

  private loadAnalysis(): void {
    this.isLoading = true;
    forkJoin({
      testingCosts: this.projectFinanceService.listTestingCosts(this.projectId),
      otherCosts: this.projectFinanceService.listOtherCosts(this.projectId),
      balance: this.projectFinanceService.getBalance(this.projectId)
    }).subscribe({
      next: ({ testingCosts, otherCosts, balance }) => {
        this.isLoading = false;
        this.testingCosts = this.projectFinanceService.readResponseKey<ProjectFinanceCost[]>(testingCosts, 'projectFinanceCosts') ?? [];
        this.otherCosts = this.projectFinanceService.readResponseKey<ProjectFinanceOtherCost[]>(otherCosts, 'projectFinanceOtherCosts') ?? [];
        this.balance = this.projectFinanceService.readResponseKey<ProjectFinanceBalance>(balance, 'balance') ?? null;
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading finance analysis:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Error loading finance analysis'));
      }
    });
  }
}
